import type { SaveData } from '../types';
import { LEVEL_COUNT } from '../../content/levels';

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const STORAGE_KEY = 'babelki-spod-zab.save';
const LEGACY_CAMPAIGN_LEVEL_COUNT = 8;

export const DEFAULT_SAVE: SaveData = {
  version: 1,
  unlockedLevel: 1,
  highScore: 0,
  muted: false,
  postcards: [],
};

export class ProgressStore {
  private readonly storage?: StorageLike;

  constructor(storage?: StorageLike) {
    this.storage = storage ?? (typeof localStorage === 'undefined' ? undefined : localStorage);
  }

  load(): SaveData {
    if (!this.storage) return { ...DEFAULT_SAVE, postcards: [] };
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_SAVE, postcards: [] };
      const parsed = JSON.parse(raw) as Partial<SaveData>;
      if (parsed.version !== 1) return { ...DEFAULT_SAVE, postcards: [] };
      const postcards = Array.isArray(parsed.postcards)
        ? [...new Set(parsed.postcards.filter((id) => Number.isInteger(id) && id >= 1 && id <= LEVEL_COUNT))]
        : [];
      const storedUnlockedLevel = clampInt(parsed.unlockedLevel, 1, LEVEL_COUNT, 1);
      const unlockedLevel =
        storedUnlockedLevel === LEGACY_CAMPAIGN_LEVEL_COUNT && postcards.includes(LEGACY_CAMPAIGN_LEVEL_COUNT)
          ? LEGACY_CAMPAIGN_LEVEL_COUNT + 1
          : storedUnlockedLevel;
      return {
        version: 1,
        unlockedLevel,
        highScore: clampInt(parsed.highScore, 0, Number.MAX_SAFE_INTEGER, 0),
        muted: Boolean(parsed.muted),
        postcards,
      };
    } catch {
      return { ...DEFAULT_SAVE, postcards: [] };
    }
  }

  save(data: SaveData): void {
    this.storage?.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  recordLevelComplete(levelId: number, score: number): SaveData {
    const save = this.load();
    const next: SaveData = {
      ...save,
      unlockedLevel: Math.max(save.unlockedLevel, Math.min(LEVEL_COUNT, levelId + 1)),
      highScore: Math.max(save.highScore, Math.max(0, Math.floor(score))),
      postcards: [...new Set([...save.postcards, levelId])].sort((a, b) => a - b),
    };
    this.save(next);
    return next;
  }

  setMuted(muted: boolean): SaveData {
    const next = { ...this.load(), muted };
    this.save(next);
    return next;
  }
}

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(value)));
}
